import { useCallback, useEffect, useMemo, useState } from "react";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDay(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

interface Args {
  days: string[];
  todayIso: string;
  visibleMonths: string[];
  selectedMonth: string;
  goToMonth: (iso: string, pos?: "end") => void;
}

// The sliding day window over one month of the By Day grid: which days are
// on screen, the prev/next arrows (rolling into the adjacent month at the
// edges), and the "Sep 14 – Sep 30" range label.
export function useDayWindow({ days, todayIso, visibleMonths, selectedMonth, goToMonth }: Args) {
  // Arriving from the previous month's "next" edge (?pos=end) lands us at the
  // END of this month. Seed windowStart high to avoid a first-frame flash at the
  // start, and keep pinToEnd set so the window stays glued to the last page as
  // the responsive dayCount settles (it can grow on narrow screens, which would
  // otherwise leave a plain clamp short of the true end). Any arrow click unpins.
  const startAtEnd =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("pos") === "end";
  // Days before today are drawn read-only; the window opens on today so the
  // editable days are in view, with elapsed days reachable via the left arrow.
  const todayIndex = Math.max(0, days.findIndex((d) => d >= todayIso));
  const [windowStart, setWindowStart] = useState(startAtEnd ? Number.MAX_SAFE_INTEGER : todayIndex);
  const [pinToEnd, setPinToEnd] = useState(startAtEnd);
  const [dayCount, setDayCount] = useState(14);

  const maxWindowStart = Math.max(0, days.length - dayCount);

  useEffect(() => {
    if (pinToEnd) {
      setWindowStart(maxWindowStart);
    } else {
      setWindowStart((s) => Math.min(s, maxWindowStart));
    }
  }, [maxWindowStart, pinToEnd]);

  const visibleDays = useMemo(
    () => days.slice(windowStart, windowStart + dayCount),
    [days, windowStart, dayCount],
  );

  const canPrev = windowStart > 0;
  const canNext = windowStart < maxWindowStart;

  const rangeLabel = useMemo(() => {
    if (visibleDays.length === 0) return "";
    const first = visibleDays[0];
    const last = visibleDays[visibleDays.length - 1];
    return first === last ? formatDay(first) : `${formatDay(first)} – ${formatDay(last)}`;
  }, [visibleDays]);

  // The day-window arrows roll over into the adjacent month at the month's
  // edges. visibleMonths is bounded by the plan period (and by the current
  // month), so this can't step outside the plan: at the first/last month the
  // corresponding arrow simply disables. Backward roll-over lands at the end of
  // the previous month (?pos=end); forward lands at its start.
  const monthIdx = visibleMonths.indexOf(selectedMonth);
  const hasPrevMonth = monthIdx > 0;
  const hasNextMonth = monthIdx >= 0 && monthIdx < visibleMonths.length - 1;

  const onPrev = useCallback(() => {
    setPinToEnd(false);
    if (canPrev) {
      setWindowStart((s) => Math.max(0, s - dayCount));
    } else if (hasPrevMonth) {
      goToMonth(visibleMonths[monthIdx - 1], "end");
    }
  }, [canPrev, dayCount, hasPrevMonth, goToMonth, visibleMonths, monthIdx]);

  const onNext = useCallback(() => {
    setPinToEnd(false);
    if (canNext) {
      setWindowStart((s) => Math.min(s + dayCount, maxWindowStart));
    } else if (hasNextMonth) {
      goToMonth(visibleMonths[monthIdx + 1]);
    }
  }, [canNext, dayCount, maxWindowStart, hasNextMonth, goToMonth, visibleMonths, monthIdx]);

  return { visibleDays, setDayCount, rangeLabel, canPrev, canNext, hasPrevMonth, hasNextMonth, onPrev, onNext };
}
